import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Search, Edit2, Trash2, Eye, ChevronLeft, ChevronRight,
  Shield, CheckCircle, XCircle, RefreshCw,
} from 'lucide-react';
import { employeeApi, SCREENS, DEPARTMENTS, DESIGNATIONS } from '../api/employeeApi';
import { getPermissions } from '../api/permissions';

// ── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_BADGE = {
  admin:    'bg-purple-100 text-purple-700',
  manager:  'bg-blue-100 text-blue-700',
  employee: 'bg-green-100 text-green-700',
};

const DEFAULT_FORM = {
  name: '', email: '', phone: '', password: '', department: '',
  designation: '', role: 'employee', is_active: true,
};

const DEFAULT_SCREENS = SCREENS.map((s) => ({
  screen_name: s, can_view: false, can_create: false, can_edit: false, can_delete: false, advanced_access: false, from_date: '', to_date: '', include_null_date: false
}));

// ── Sub-components ────────────────────────────────────────────────────────────

function Badge({ role }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_BADGE[role] || 'bg-gray-100 text-gray-600'}`}>
      {role}
    </span>
  );
}

function StatusDot({ active }) {
  return active
    ? <span className="flex items-center gap-1 text-green-600 text-sm"><CheckCircle size={14} /> Active</span>
    : <span className="flex items-center gap-1 text-red-500 text-sm"><XCircle size={14} /> Inactive</span>;
}

// ── Modal: Add / Edit Employee ────────────────────────────────────────────────

function EmployeeModal({ employee, onClose, onSaved }) {
  const isEdit = !!employee;
  const [form, setForm] = useState(isEdit ? { ...employee, password: '' } : { ...DEFAULT_FORM });
  const [screenAccess, setScreenAccess] = useState(DEFAULT_SCREENS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && employee.id) {
      employeeApi.getById(employee.id).then((res) => {
        if (res.success && res.data?.screen_access?.length) {
          setScreenAccess(
            SCREENS.map((s) => {
              const found = res.data.screen_access.find((x) => x.screen_name === s);
              return found
                ? { screen_name: s, can_view: !!+found.can_view, can_create: !!+found.can_create, can_edit: !!+found.can_edit, can_delete: !!+found.can_delete, advanced_access: !!+found.advanced_access, from_date: found.from_date || '', to_date: found.to_date || '', include_null_date: !!+found.include_null_date }
                : { screen_name: s, can_view: false, can_create: false, can_edit: false, can_delete: false, advanced_access: false, from_date: '', to_date: '', include_null_date: false };
            })
          );
        }
      });
    }
  }, [isEdit, employee]);

  const togglePerm = (idx, perm) => {
    setScreenAccess((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [perm]: !s[perm] } : s))
    );
  };

  const setDate = (idx, field, val) => {
    setScreenAccess((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s))
    );
  };

  const toggleAll = (idx) => {
    const s = screenAccess[idx];
    const allOn = s.can_view && s.can_create && s.can_edit && s.can_delete;
    setScreenAccess((prev) =>
      prev.map((x, i) =>
        i === idx ? { ...x, can_view: !allOn, can_create: !allOn, can_edit: !allOn, can_delete: !allOn } : x
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    const custScreen = screenAccess.find(s => s.screen_name === 'Customers');
    if (custScreen && custScreen.advanced_access) {
      if (!custScreen.from_date || !custScreen.to_date) {
        return setError('From Date and To Date are required when Advanced access is enabled for Customers.');
      }
      if (new Date(custScreen.from_date) > new Date(custScreen.to_date)) {
        return setError('From Date cannot be greater than To Date.');
      }
    }

    setLoading(true);

    const payload = { ...form, is_active: form.is_active ? 1 : 0 };
    if (!isEdit) {
      payload.screen_access = screenAccess.map((s) => ({
        ...s, can_view: s.can_view ? 1 : 0, can_create: s.can_create ? 1 : 0,
        can_edit: s.can_edit ? 1 : 0, can_delete: s.can_delete ? 1 : 0,
        advanced_access: s.advanced_access ? 1 : 0,
        from_date: s.from_date || null,
        to_date: s.to_date || null,
        include_null_date: s.include_null_date ? 1 : 0,
      }));
    }
    if (isEdit && !payload.password) delete payload.password;

    const res = isEdit
      ? await employeeApi.update(employee.id, payload)
      : await employeeApi.create(payload);

    if (res.success) {
      if (isEdit) {
        await employeeApi.updateScreenAccess(employee.id, screenAccess.map((s) => ({
          ...s, can_view: s.can_view ? 1 : 0, can_create: s.can_create ? 1 : 0,
          can_edit: s.can_edit ? 1 : 0, can_delete: s.can_delete ? 1 : 0,
          advanced_access: s.advanced_access ? 1 : 0,
          from_date: s.from_date || null,
          to_date: s.to_date || null,
          include_null_date: s.include_null_date ? 1 : 0,
        })));
      }
      onSaved();
    } else {
      setError(res.message || res.error || 'Something went wrong');
    }
    setLoading(false);
  };

  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400';
  const sel = inp + ' bg-white';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Edit Employee' : 'Add New Employee'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg">{error}</div>}

          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Full Name *</label>
                <input className={inp} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Email *</label>
                <input className={inp} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@bank.com" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                <input className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9000000000" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input className={inp} type="password" required={!isEdit} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={isEdit ? '••••••••' : 'Min 6 chars'} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Department</label>
                <select className={sel} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select department</option>
                  {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Designation</label>
                <select className={sel} value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}>
                  <option value="">Select designation</option>
                  {DESIGNATIONS.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Role</label>
                <select className={sel} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <div
                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    className={`w-11 h-6 rounded-full transition-colors ${form.is_active ? 'bg-primary-500' : 'bg-gray-300'} relative`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Screen Access */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield size={16} className="text-primary-500" />
              <h3 className="text-sm font-semibold text-gray-700">Screen Access Permissions</h3>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium w-40">Screen</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">All</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">View</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">Create</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">Edit</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">Delete</th>
                    <th className="text-center px-3 py-2 text-gray-500 font-medium">Advanced</th>
                  </tr>
                </thead>
                <tbody>
                  {screenAccess.map((s, idx) => {
                    const allOn = s.can_view && s.can_create && s.can_edit && s.can_delete;
                    return (
                      <React.Fragment key={s.screen_name}>
                        <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium text-gray-700">{s.screen_name}</td>
                          {[['all', allOn], ['can_view', s.can_view], ['can_create', s.can_create], ['can_edit', s.can_edit], ['can_delete', s.can_delete]].map(([perm, val]) => (
                            <td key={perm} className="text-center px-3 py-2">
                              <input
                                type="checkbox"
                                checked={!!val}
                                onChange={() => perm === 'all' ? toggleAll(idx) : togglePerm(idx, perm)}
                                className="w-4 h-4 accent-primary-500 cursor-pointer"
                              />
                            </td>
                          ))}
                          <td className="text-center px-3 py-2">
                            {s.screen_name === 'Customers' && (
                              <input
                                type="checkbox"
                                checked={!!s.advanced_access}
                                onChange={() => togglePerm(idx, 'advanced_access')}
                                className="w-4 h-4 accent-primary-500 cursor-pointer"
                              />
                            )}
                          </td>
                        </tr>
                        {s.screen_name === 'Customers' && s.advanced_access && (
                          <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td colSpan={7} className="px-4 pb-3 pt-1 border-t-0">
                              <div className="flex flex-col gap-3 bg-gray-100 p-3 rounded-lg ml-4 mr-2">
                                <div className="flex items-center gap-4">
                                  <div className="flex-1 flex items-center gap-2">
                                    <label className="text-xs text-gray-600 font-medium w-20">From Date:</label>
                                    <input type="date" className={inp + ' flex-1 bg-white'} value={s.from_date} onChange={(e) => setDate(idx, 'from_date', e.target.value)} required={s.advanced_access} />
                                  </div>
                                  <div className="flex-1 flex items-center gap-2">
                                    <label className="text-xs text-gray-600 font-medium w-16">To Date:</label>
                                    <input type="date" className={inp + ' flex-1 bg-white'} value={s.to_date} onChange={(e) => setDate(idx, 'to_date', e.target.value)} required={s.advanced_access} min={s.from_date} />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 pl-2">
                                  <input
                                    type="checkbox"
                                    id={`include_null_${idx}`}
                                    checked={!!s.include_null_date}
                                    onChange={() => togglePerm(idx, 'include_null_date')}
                                    className="w-4 h-4 accent-primary-500 cursor-pointer"
                                  />
                                  <label htmlFor={`include_null_${idx}`} className="text-xs text-gray-600 cursor-pointer">
                                    Include if Application Date not extracted (—)
                                  </label>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-6 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2">
              {loading && <RefreshCw size={14} className="animate-spin" />}
              {loading ? 'Saving...' : isEdit ? 'Update Employee' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal: View Employee ──────────────────────────────────────────────────────

function ViewModal({ employeeId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    employeeApi.getById(employeeId).then((res) => {
      if (res.success) setData(res.data);
    });
  }, [employeeId]);

  if (!data) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-8"><RefreshCw className="animate-spin text-primary-500" /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Employee Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xl font-bold">
              {data.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-gray-900 text-lg">{data.name}</p>
              <p className="text-sm text-gray-500">{data.employee_id}</p>
              <Badge role={data.role} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Email', data.email],
              ['Phone', data.phone || '—'],
              ['Department', data.department || '—'],
              ['Designation', data.designation || '—'],
              ['Status', data.is_active ? 'Active' : 'Inactive'],
              ['Joined', new Date(data.created_at).toLocaleDateString()],
            ].map(([label, val]) => (
              <div key={label} className="bg-gray-50 rounded-lg px-4 py-3">
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <p className="font-medium text-gray-800">{val}</p>
              </div>
            ))}
          </div>

          {data.screen_access?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield size={14} className="text-primary-500" />
                <p className="text-sm font-semibold text-gray-700">Screen Access</p>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-gray-500">Screen</th>
                      <th className="text-center px-3 py-2 text-gray-500">View</th>
                      <th className="text-center px-3 py-2 text-gray-500">Create</th>
                      <th className="text-center px-3 py-2 text-gray-500">Edit</th>
                      <th className="text-center px-3 py-2 text-gray-500">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.screen_access.map((s, i) => (
                      <tr key={s.screen_name} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 font-medium text-gray-700">{s.screen_name}</td>
                        {['can_view', 'can_create', 'can_edit', 'can_delete'].map((p) => (
                          <td key={p} className="text-center px-3 py-2">
                            {+s[p] ? <CheckCircle size={13} className="text-green-500 mx-auto" /> : <XCircle size={13} className="text-gray-300 mx-auto" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function EmployeesPage() {
  const { canCreate, canEdit, canDelete, canView } = getPermissions('Employees');
  const [employees, setEmployees] = useState(() => {
    const cached = sessionStorage.getItem('employees_cache');
    return cached ? JSON.parse(cached) : [];
  });
  const [pagination, setPagination] = useState(() => {
    const cached = sessionStorage.getItem('employees_pagination');
    return cached ? JSON.parse(cached) : { page: 1, total: 0, pages: 1 };
  });
  const [loading, setLoading] = useState(() => !sessionStorage.getItem('employees_cache'));
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [modal, setModal] = useState(null); // null | { type: 'add' | 'edit' | 'view', employee? }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchEmployees = useCallback(async (page = 1, limit = perPage) => {
    if (employees.length === 0) setLoading(true);
    const res = await employeeApi.getAll(page, limit, filterRole, filterDept);
    if (res.success) {
      setEmployees(res.data);
      sessionStorage.setItem('employees_cache', JSON.stringify(res.data));
      setPagination(res.pagination);
      sessionStorage.setItem('employees_pagination', JSON.stringify(res.pagination));
    }
    setLoading(false);
  }, [filterRole, filterDept]);

  useEffect(() => { fetchEmployees(1, perPage); }, [fetchEmployees, perPage]);

  const handleDelete = async (id) => {
    const res = await employeeApi.delete(id);
    if (res.success) {
      setDeleteConfirm(null);
      fetchEmployees(pagination.page);
    }
  };

  const filtered = search
    ? employees.filter((e) =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.email.toLowerCase().includes(search.toLowerCase()) ||
        (e.employee_id || '').toLowerCase().includes(search.toLowerCase())
      )
    : employees;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Employees</h2>
          <p className="text-sm text-gray-500 mt-0.5">{pagination.total} total employees</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setModal({ type: 'add' })}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <UserPlus size={16} /> Add Employee
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-48 border border-gray-200 rounded-lg px-3 py-2">
          <Search size={15} className="text-gray-400" />
          <input
            className="flex-1 text-sm outline-none"
            placeholder="Search name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 bg-white"
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="employee">Employee</option>
        </select>
        <select
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 bg-white"
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
        >
          <option value="">All Departments</option>
          {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
        </select>
        <button onClick={() => fetchEmployees(pagination.page)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw className="animate-spin mr-2" size={20} /> Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg">No employees found</p>
            <p className="text-sm mt-1">Add your first employee to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-gray-500 font-medium">Employee</th>
                <th className="text-left px-5 py-3 text-gray-500 font-medium">Department</th>
                <th className="text-left px-5 py-3 text-gray-500 font-medium">Role</th>
                <th className="text-left px-5 py-3 text-gray-500 font-medium">Status</th>
                <th className="text-right px-5 py-3 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm">
                        {emp.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-400">{emp.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-600">{emp.department || '—'} {emp.designation ? `· ${emp.designation}` : ''}</td>
                  <td className="px-5 py-3"><Badge role={emp.role} /></td>
                  <td className="px-5 py-3"><StatusDot active={+emp.is_active === 1} /></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canView && (
                        <button
                          onClick={() => setModal({ type: 'view', employee: emp })}
                          className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors"
                          title="View"
                        >
                          <Eye size={15} />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => setModal({ type: 'edit', employee: emp })}
                          className="p-2 rounded-lg hover:bg-amber-50 text-gray-400 hover:text-amber-500 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={15} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => setDeleteConfirm(emp)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="border-t border-gray-100 px-5 py-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              {[5, 10, 15].map(n => (
                <button
                  key={n}
                  onClick={() => { setPerPage(n); fetchEmployees(1, n); }}
                  className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                    perPage === n
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-200 hover:bg-gray-50 text-gray-600'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs">
                {(pagination.page - 1) * perPage + 1}–{Math.min(pagination.page * perPage, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => fetchEmployees(pagination.page - 1, perPage)}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  disabled={pagination.page === pagination.pages}
                  onClick={() => fetchEmployees(pagination.page + 1, perPage)}
                  className="p-1.5 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === 'add' && (
        <EmployeeModal onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchEmployees(1); }} />
      )}
      {modal?.type === 'edit' && (
        <EmployeeModal employee={modal.employee} onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchEmployees(pagination.page); }} />
      )}
      {modal?.type === 'view' && (
        <ViewModal employeeId={modal.employee.id} onClose={() => setModal(null)} />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-gray-900 text-lg mb-2">Delete Employee</h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
